/**
 * Cliente da API do painel admin.
 * Carrega e salva conteúdo (conteudo) e entidades (unidades, profissionais, etc.) no backend/BD.
 */
import { fetchApi, parseError } from '@/api/http'
import { getTenantSlug } from '@/config/tenant'
import { markAdminSessionActive } from '@/utils/adminAuth'
import { forceRelogin } from '@/utils/authFlow'
import { resolveMediaUrl, toStorableMediaUrl } from '@/utils/mediaUrl'
import { mergeConteudoSnapshot, notifyConteudoUpdated } from '@/utils/conteudoSync'

const URL_CONTENT_KEYS = new Set([
  'menu_banner_imagem_url',
  'quem_somos_imagem_url',
  'app_logo_url',
  'login_quick_action_1_image_url',
  'login_quick_action_2_image_url',
  'login_quick_action_3_image_url',
])

async function fetchAdmin(path: string, options?: RequestInit): Promise<Response> {
  const headers: Record<string, string> = {
    'X-Tenant-Slug': getTenantSlug(),
    ...(options?.headers as Record<string, string> | undefined),
  }
  return fetchApi(path, { ...options, headers })
}

async function handleAdminResponse<T>(res: Response): Promise<T> {
  const text = await res.text()
  if (res.status === 401 || res.status === 403) {
    const msg = (() => {
      try {
        const d = JSON.parse(text) as { error?: string }
        return d?.error ?? 'Sessão de administrador inválida ou expirada. Faça login novamente.'
      } catch {
        return 'Sessão de administrador inválida ou expirada. Faça login novamente.'
      }
    })()
    forceRelogin('admin', msg)
  }
  if (!res.ok) return parseError(res, text)
  if (res.status === 204 || !text) return undefined as T
  const trimmed = text.trim()
  if (trimmed.startsWith('<')) {
    throw new Error(
      'A API não respondeu. 1) Inicie o servidor: npm run dev:server (ou npm run dev:all). 2) Reinicie o frontend (Ctrl+C e depois npm run dev) para carregar o .env.'
    )
  }
  try {
    return JSON.parse(text) as T
  } catch {
    throw new Error('Resposta da API não é JSON válido.')
  }
}

// --- Login admin (não usa token; devolve token) ---

export async function loginAdmin(
  email: string,
  senha: string,
  tenantSlug?: string
): Promise<{
  email: string
  tenantId?: number
  tenantSlug?: string
  tenantNome?: string
}> {
  const slug = (tenantSlug ?? getTenantSlug()).trim().toLowerCase()
  const res = await fetchApi('/api/admin/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
    body: JSON.stringify({ email: email.trim(), senha, tenantSlug: slug }),
  })
  const text = await res.text()
  if (!res.ok) {
    const data = text ? ((): { error?: string } => { try { return JSON.parse(text) } catch { return { error: text } } })() : { error: res.statusText }
    throw new Error(data?.error ?? 'Login falhou.')
  }
  const data = JSON.parse(text) as {
    email: string
    tenantId?: number
    tenantSlug?: string
    tenantNome?: string
  }
  markAdminSessionActive()
  return data
}

/** Valida cookie httpOnly de sessão admin (ex.: nova aba após impersonate). */
export async function verifyAdminSession(): Promise<{
  ok: boolean
  email: string
  tenantId: number
  tenantSlug: string
}> {
  const res = await fetchApi('/api/admin/session')
  const text = await res.text()
  if (!res.ok) {
    throw new Error('Sessão de administrador inválida ou expirada.')
  }
  return JSON.parse(text) as {
    ok: boolean
    email: string
    tenantId: number
    tenantSlug: string
  }
}

// --- Conteúdo (tabela conteudo: menu_banner_texto, quem_somos, etc.) ---

export const CONTEUDO_CHAVES = {
  menu_banner_texto: 'menu_banner_texto',
  menu_banner_imagem_url: 'menu_banner_imagem_url',
  app_logo_url: 'app_logo_url',
  menu_show_quem_somos: 'menu_show_quem_somos',
  menu_show_servicos: 'menu_show_servicos',
  menu_show_contatos: 'menu_show_contatos',
  menu_show_mapa: 'menu_show_mapa',
  menu_show_galeria: 'menu_show_galeria',
  menu_show_social: 'menu_show_social',
  menu_show_minha_conta: 'menu_show_minha_conta',
  /** Legado — se os três flags abaixo não existirem, usa-se este valor para todos */
  agendamentos_enabled: 'agendamentos_enabled',
  agendamento_fluxo_enabled: 'agendamento_fluxo_enabled',
  historico_cliente_enabled: 'historico_cliente_enabled',
  servicos_marcacao_enabled: 'servicos_marcacao_enabled',
  notificacoes_enabled: 'notificacoes_enabled',
  vip_enabled: 'vip_enabled',
  login_quick_actions_enabled: 'login_quick_actions_enabled',
  login_quick_action_1_label: 'login_quick_action_1_label',
  login_quick_action_1_link: 'login_quick_action_1_link',
  login_quick_action_1_image_url: 'login_quick_action_1_image_url',
  login_quick_action_2_label: 'login_quick_action_2_label',
  login_quick_action_2_link: 'login_quick_action_2_link',
  login_quick_action_2_image_url: 'login_quick_action_2_image_url',
  login_quick_action_3_label: 'login_quick_action_3_label',
  login_quick_action_3_link: 'login_quick_action_3_link',
  login_quick_action_3_image_url: 'login_quick_action_3_image_url',
  pacotes_enabled: 'pacotes_enabled',
  tarefas_enabled: 'tarefas_enabled',
  quem_somos: 'quem_somos',
  quem_somos_imagem_url: 'quem_somos_imagem_url',
  servicos_intro: 'servicos_intro',
  contatos: 'contatos',
  mapa_url: 'mapa_url',
  vip_texto: 'vip_texto',
  categorias: 'categorias',
} as const

/** Conteúdo público (ex.: ecrã de login) — um único GET /api/conteudo em vez de N pedidos por chave. */
export async function getConteudoPublic(): Promise<Record<string, string | null>> {
  const res = await fetchApi('/api/conteudo')
  const text = await res.text()
  if (!res.ok) {
    try {
      await parseError(res, text)
    } catch {
      /* offline ou API indisponível */
    }
    return {}
  }
  if (!text.trim()) return {}
  let data: Record<string, string | null>
  try {
    data = JSON.parse(text) as Record<string, string | null>
  } catch {
    return {}
  }
  const normalized = { ...data }
  for (const key of URL_CONTENT_KEYS) {
    const current = normalized[key]
    if (typeof current === 'string' && current.trim()) normalized[key] = resolveMediaUrl(current)
  }
  mergeConteudoSnapshot(normalized)
  return normalized
}

/** Retorna todo o conteúdo (chave -> valor) */
export async function getConteudo(): Promise<Record<string, string | null>> {
  const res = await fetchAdmin('/api/conteudo')
  const data = await handleAdminResponse<Record<string, string | null>>(res)
  const normalized = { ...data }
  for (const key of URL_CONTENT_KEYS) {
    const current = normalized[key]
    if (typeof current === 'string' && current.trim()) normalized[key] = resolveMediaUrl(current)
  }
  return normalized
}

/** Retorna o valor de uma chave */
export async function getConteudoChave(chave: string): Promise<string | null> {
  const res = await fetchAdmin(`/api/conteudo/${encodeURIComponent(chave)}`)
  const data = await handleAdminResponse<{ valor: string | null }>(res)
  if (!data?.valor) return null
  if (URL_CONTENT_KEYS.has(chave)) return resolveMediaUrl(data.valor)
  return data.valor
}

/** Atualiza o valor de uma chave (body: { valor }) */
export async function putConteudoChave(
  chave: string,
  valor: string | null,
  options?: { notify?: boolean }
): Promise<void> {
  const nextValue =
    URL_CONTENT_KEYS.has(chave) && typeof valor === 'string' && valor.trim()
      ? toStorableMediaUrl(valor)
      : valor
  const res = await fetchAdmin(`/api/conteudo/${encodeURIComponent(chave)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ valor: nextValue ?? '' }),
  })
  await handleAdminResponse(res)
  if (options?.notify !== false) notifyConteudoUpdated()
}

/**
 * POST /api/upload - envia uma imagem e devolve:
 *  - url:      caminho relativo (`/uploads/...`) para guardar na BD
 *  - preview:  URL absoluta pronta para usar como `<img src>` no momento
 */
export async function uploadImage(file: File): Promise<{ url: string; preview: string }> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetchAdmin('/api/upload', {
    method: 'POST',
    body: form,
  })
  const text = await res.text()
  if (res.status === 401 || res.status === 403) {
    const msg = (() => {
      try {
        const d = JSON.parse(text) as { error?: string }
        return d?.error ?? 'Sessão de administrador inválida ou expirada. Faça login novamente.'
      } catch {
        return 'Sessão de administrador inválida ou expirada. Faça login novamente.'
      }
    })()
    forceRelogin('admin', msg)
  }
  const trimmed = text.trim()
  if (trimmed.startsWith('<')) {
    throw new Error(
      res.status === 503
        ? 'Servidor indisponível (503): o Apache não está a encaminhar /api para o Node na porta 3002. Corrija ProxyPass e reinicie o Apache.'
        : 'A API devolveu HTML em vez de JSON. Verifique no servidor: ProxyPass /api http://127.0.0.1:3002/api e pm2 restart agendamento-api.'
    )
  }
  if (!res.ok) {
    try {
      const data = JSON.parse(text) as { error?: string }
      throw new Error(data?.error ?? (text || 'Erro no upload.'))
    } catch (e) {
      if (e instanceof Error && e.message !== 'Erro no upload.' && !e.message.startsWith('A API')) throw e
      throw new Error(text || `Erro no upload (${res.status}).`)
    }
  }
  let data: { url: string }
  try {
    data = JSON.parse(text) as { url: string }
  } catch {
    throw new Error('Resposta inválida do servidor no upload.')
  }
  const storable = toStorableMediaUrl(data.url)
  return { url: storable, preview: resolveMediaUrl(storable) }
}

// --- Unidades ---

export interface Unidade {
  id: number
  nome: string
  morada: string | null
  mapa_url: string | null
  ativo: number
  ordem: number
}

export async function getUnidades(includeInativos = true): Promise<Unidade[]> {
  const q = includeInativos ? '?ativo=false' : ''
  const res = await fetchAdmin(`/api/unidades${q}`)
  return handleAdminResponse<Unidade[]>(res)
}

export async function createUnidade(body: { nome: string; morada?: string; mapa_url?: string; ativo?: boolean; ordem?: number }): Promise<Unidade> {
  const res = await fetchAdmin('/api/unidades', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse<Unidade>(res)
}

export async function updateUnidade(id: number, body: Partial<{ nome: string; morada: string; mapa_url: string; ativo: boolean; ordem: number }>): Promise<Unidade> {
  const res = await fetchAdmin(`/api/unidades/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse<Unidade>(res)
}

export async function deleteUnidade(id: number): Promise<void> {
  const res = await fetchAdmin(`/api/unidades/${id}`, { method: 'DELETE' })
  await handleAdminResponse(res)
}

// --- Profissionais ---

export interface Profissional {
  id: number
  unidade_id: number | null
  nome: string
  especialidade: string | null
  foto_url?: string | null
  ativo: number
  ordem: number
  email: string | null
}

function mapProfissional(p: Profissional): Profissional {
  return {
    ...p,
    foto_url: p.foto_url ? resolveMediaUrl(p.foto_url) : null,
  }
}

export async function getProfissionais(includeInativos = true, unidadeId?: number): Promise<Profissional[]> {
  const params = new URLSearchParams()
  if (includeInativos) params.set('ativo', 'false')
  if (unidadeId != null && unidadeId > 0) params.set('unidade_id', String(unidadeId))
  const q = params.toString() ? `?${params.toString()}` : ''
  const res = await fetchAdmin(`/api/profissionais${q}`)
  const rows = await handleAdminResponse<Profissional[]>(res)
  return rows.map(mapProfissional)
}

export async function createProfissional(body: {
  unidade_id?: number | null
  nome: string
  especialidade?: string
  foto_url?: string | null
  email?: string | null
  senha?: string
  ativo?: boolean
  ordem?: number
}): Promise<Profissional> {
  const res = await fetchAdmin('/api/profissionais', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...body,
      foto_url: body.foto_url ? toStorableMediaUrl(body.foto_url) : body.foto_url ?? undefined,
    }),
  })
  return mapProfissional(await handleAdminResponse<Profissional>(res))
}

export async function updateProfissional(
  id: number,
  body: Partial<{
    unidade_id: number | null
    nome: string
    especialidade: string
    foto_url: string | null
    email: string | null
    senha: string
    ativo: boolean
    ordem: number
  }>
): Promise<Profissional> {
  const payload = {
    ...body,
    foto_url:
      body.foto_url !== undefined
        ? body.foto_url
          ? toStorableMediaUrl(body.foto_url)
          : null
        : undefined,
  }
  const res = await fetchAdmin(`/api/profissionais/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return mapProfissional(await handleAdminResponse<Profissional>(res))
}

export async function deleteProfissional(id: number): Promise<void> {
  const res = await fetchAdmin(`/api/profissionais/${id}`, { method: 'DELETE' })
  await handleAdminResponse(res)
}

// --- Serviços ---

export interface Servico {
  id: number
  nome: string
  descricao: string | null
  preco?: number | string | null
  duracao_minutos?: number | null
  categoria: string | null
  icone: string | null
  ativo: number
  ordem: number
}

export async function getServicos(includeInativos = true): Promise<Servico[]> {
  const q = includeInativos ? '?ativo=false' : ''
  const res = await fetchAdmin(`/api/servicos${q}`)
  return handleAdminResponse<Servico[]>(res)
}

export async function createServico(body: {
  nome: string
  descricao?: string
  categoria?: string
  icone?: string
  ativo?: boolean
  ordem?: number
  preco?: number | null
  duracao_minutos?: number | null
}): Promise<Servico> {
  const res = await fetchAdmin('/api/servicos', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse<Servico>(res)
}

export async function updateServico(
  id: number,
  body: Partial<{
    nome: string
    descricao: string
    categoria: string
    icone: string
    ativo: boolean
    ordem: number
    preco: number | null
    duracao_minutos: number | null
  }>
): Promise<Servico> {
  const res = await fetchAdmin(`/api/servicos/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse<Servico>(res)
}

export async function deleteServico(id: number): Promise<void> {
  const res = await fetchAdmin(`/api/servicos/${id}`, { method: 'DELETE' })
  await handleAdminResponse(res)
}

// --- Pacotes ---

export interface Pacote {
  id: number
  nome: string
  descricao: string | null
  imagem_url?: string | null
  servicos_ids?: number[]
  preco: number | null
  ativo: number
  ordem: number
}

export async function getPacotes(includeInativos = true): Promise<Pacote[]> {
  const q = includeInativos ? '?ativo=false' : ''
  const res = await fetchAdmin(`/api/pacotes${q}`)
  return handleAdminResponse<Pacote[]>(res)
}

export async function createPacote(body: {
  nome: string
  descricao?: string
  preco?: number
  ativo?: boolean
  ordem?: number
  imagem_url?: string | null
  servicos_ids?: number[]
}): Promise<Pacote> {
  const res = await fetchAdmin('/api/pacotes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse<Pacote>(res)
}

export async function updatePacote(
  id: number,
  body: Partial<{
    nome: string
    descricao: string
    preco: number
    ativo: boolean
    ordem: number
    imagem_url: string | null
    servicos_ids: number[]
  }>
): Promise<Pacote> {
  const res = await fetchAdmin(`/api/pacotes/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse<Pacote>(res)
}

export async function deletePacote(id: number): Promise<void> {
  const res = await fetchAdmin(`/api/pacotes/${id}`, { method: 'DELETE' })
  await handleAdminResponse(res)
}

// --- Galeria ---

export interface GaleriaItem {
  id: number
  url: string
  tipo: 'imagem' | 'video'
  legenda: string | null
  ordem: number
  ativo: number
}

export async function getGaleria(includeInativos = true): Promise<GaleriaItem[]> {
  const q = includeInativos ? '?ativo=false' : ''
  const res = await fetchAdmin(`/api/galeria${q}`)
  const items = await handleAdminResponse<GaleriaItem[]>(res)
  return items.map((item) => ({ ...item, url: resolveMediaUrl(item.url) }))
}

export async function createGaleriaItem(body: { url: string; tipo?: 'imagem' | 'video'; legenda?: string; ordem?: number; ativo?: boolean }): Promise<GaleriaItem> {
  const normalizedBody = {
    ...body,
    url: toStorableMediaUrl(body.url),
  }
  const res = await fetchAdmin('/api/galeria', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(normalizedBody),
  })
  const item = await handleAdminResponse<GaleriaItem>(res)
  return { ...item, url: resolveMediaUrl(item.url) }
}

export async function updateGaleriaItem(id: number, body: Partial<{ url: string; tipo: 'imagem' | 'video'; legenda: string; ordem: number; ativo: boolean }>): Promise<GaleriaItem> {
  const normalizedBody = body.url
    ? { ...body, url: toStorableMediaUrl(body.url) }
    : body
  const res = await fetchAdmin(`/api/galeria/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(normalizedBody),
  })
  const item = await handleAdminResponse<GaleriaItem>(res)
  return { ...item, url: resolveMediaUrl(item.url) }
}

export async function deleteGaleriaItem(id: number): Promise<void> {
  const res = await fetchAdmin(`/api/galeria/${id}`, { method: 'DELETE' })
  await handleAdminResponse(res)
}

// --- Feedbacks (social proof) ---

export interface FeedbackItem {
  id: number
  usuario_id: number | null
  nome: string
  texto: string
  aprovado: number
  ativo: number
  destaque: number
  criado_em: string
}

export async function updateFeedbackAdmin(
  id: number,
  body: Partial<{ aprovado: boolean; ativo: boolean; destaque: boolean }>
): Promise<FeedbackItem> {
  const res = await fetchAdmin(`/api/feedbacks/admin/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse<FeedbackItem>(res)
}

// --- Agendamentos (admin) ---

export interface AdminAgendamentoItem {
  id: number
  usuario_id: number
  cliente_nome: string | null
  cliente_email: string | null
  cliente_telefone: string | null
  data: string
  hora: string
  profissional_id: number | null
  unidade_id?: number | null
  profissional: string
  unidade: string
  status: string
  servico_id?: number | null
  servico_nome?: string | null
}

export type AdminAgendamentoStatus = 'agendado' | 'realizado' | 'cancelado'

export interface AdminAgendamentosQuery {
  q?: string
  profissional_id?: number
  status?: AdminAgendamentoStatus
  data_desde?: string
  data_ate?: string
  page?: number
  limit?: number
}

export interface AdminAgendamentosListResponse {
  items: AdminAgendamentoItem[]
  total: number
  page: number
  limit: number
  pages: number
}

export async function getAgendamentosAdmin(
  query: AdminAgendamentosQuery = {}
): Promise<AdminAgendamentosListResponse> {
  const params = new URLSearchParams()
  if (query.q?.trim()) params.set('q', query.q.trim())
  if (query.profissional_id != null && query.profissional_id > 0) {
    params.set('profissional_id', String(query.profissional_id))
  }
  if (query.status) params.set('status', query.status)
  if (query.data_desde) params.set('data_desde', query.data_desde)
  if (query.data_ate) params.set('data_ate', query.data_ate)
  if (query.page) params.set('page', String(query.page))
  if (query.limit) params.set('limit', String(query.limit))
  const qs = params.toString()
  const res = await fetchAdmin(`/api/agendamentos/admin${qs ? `?${qs}` : ''}`)
  return handleAdminResponse<AdminAgendamentosListResponse>(res)
}

export async function createAgendamentoAdmin(body: {
  usuario_id: number
  profissional_id: number
  unidade_id: number
  data: string
  hora: string
  servico_id?: number | null
  status?: AdminAgendamentoStatus
}): Promise<{ id: number; status: string }> {
  const res = await fetchAdmin('/api/agendamentos/admin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse(res)
}

export async function updateAgendamentoAdmin(
  id: number,
  body: Partial<{
    data: string
    hora: string
    profissional_id: number
    unidade_id: number
    servico_id: number | null
    status: AdminAgendamentoStatus
  }>
): Promise<{ id: number; data: string; hora: string; status: string }> {
  const res = await fetchAdmin(`/api/agendamentos/admin/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse(res)
}

export async function updateAgendamentoStatusAdmin(
  id: number,
  status: AdminAgendamentoStatus
): Promise<{ id: number; status: string }> {
  const res = await fetchAdmin(`/api/agendamentos/admin/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
  return handleAdminResponse(res)
}

// --- Social posts (admin moderation) ---

export interface SocialPostAdminItem {
  id: number
  usuario_id: number
  usuario_nome: string
  texto: string | null
  media_url: string | null
  aprovado: number
  ativo: number
  destaque: number
  criado_em: string
}

export async function updateSocialPostAdmin(
  id: number,
  body: Partial<{ aprovado: boolean; ativo: boolean; destaque: boolean }>
): Promise<SocialPostAdminItem> {
  const res = await fetchAdmin(`/api/social/posts/admin/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse<SocialPostAdminItem>(res)
}

// --- Admin notifications ---

export interface NotificacaoAdminResumo {
  id: number
  tipo: string
  titulo: string
  mensagem: string
  agendamento_id: number | null
  lida: number
  criada_em: string
}

export async function getNotificacoesAdminCount(adminEmail: string): Promise<number> {
  const res = await fetchAdmin(`/api/notificacoes/admin/unread-count?admin_email=${encodeURIComponent(adminEmail)}`)
  const data = await handleAdminResponse<{ unread?: number }>(res)
  return typeof data?.unread === 'number' ? data.unread : 0
}

export async function getNotificacoesAdmin(adminEmail: string, limit = 10): Promise<NotificacaoAdminResumo[]> {
  const res = await fetchAdmin(`/api/notificacoes/admin?admin_email=${encodeURIComponent(adminEmail)}&limit=${limit}`)
  return handleAdminResponse<NotificacaoAdminResumo[]>(res)
}

// --- Disponibilidade (horários por profissional / dia) ---

export interface DisponibilidadeRegra {
  id: number
  profissional_id: number
  unidade_id: number | null
  unidade_nome?: string | null
  dia_semana: number
  data_especifica?: string | null
  tipo?: 'semanal' | 'especifica'
  hora_inicio: string
  hora_fim: string
  ativo: number
}

export async function getDisponibilidade(profissionalId?: number): Promise<DisponibilidadeRegra[]> {
  const q = profissionalId != null ? `?profissional_id=${profissionalId}` : ''
  const res = await fetchAdmin(`/api/disponibilidade${q}`)
  return handleAdminResponse<DisponibilidadeRegra[]>(res)
}

export async function createDisponibilidade(body: {
  profissional_id: number
  unidade_id: number
  dia_semana?: number
  data_especifica?: string | null
  hora_inicio: string
  hora_fim: string
  ativo?: boolean
}): Promise<DisponibilidadeRegra> {
  const res = await fetchAdmin('/api/disponibilidade', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse<DisponibilidadeRegra>(res)
}

export async function updateDisponibilidade(
  id: number,
  body: Partial<{
    unidade_id: number
    dia_semana: number
    data_especifica: string | null
    hora_inicio: string
    hora_fim: string
    ativo: boolean
  }>
): Promise<DisponibilidadeRegra> {
  const res = await fetchAdmin(`/api/disponibilidade/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse<DisponibilidadeRegra>(res)
}

export async function deleteDisponibilidade(id: number): Promise<void> {
  const res = await fetchAdmin(`/api/disponibilidade/${id}`, { method: 'DELETE' })
  await handleAdminResponse(res)
}

export async function copyDisponibilidade(body: {
  from_profissional_id: number
  to_profissional_id: number
  replace?: boolean
}): Promise<{ copied: number }> {
  const res = await fetchAdmin('/api/disponibilidade/copy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleAdminResponse(res)
}

// --- Dashboard & conta admin ---

export interface AdminDashboardStats {
  agendamentos_hoje: number
  agendamentos_hoje_pendentes: number
  agendamentos_pendentes: number
  feedbacks_pendentes: number
  social_pendentes: number
  usuarios_ativos: number
  profissionais_ativos: number
}

export async function getAdminDashboardStats(): Promise<AdminDashboardStats> {
  const res = await fetchAdmin('/api/admin/dashboard-stats')
  return handleAdminResponse(res)
}

export async function changeAdminPassword(senhaAtual: string, senhaNova: string): Promise<void> {
  const res = await fetchAdmin('/api/admin/password', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ senhaAtual, senhaNova }),
  })
  await handleAdminResponse(res)
}

export interface UsuarioSearchRow {
  id: number
  nome: string
  email: string
  telefone: string | null
}

export async function searchUsuariosAdmin(q: string): Promise<UsuarioSearchRow[]> {
  const res = await fetchAdmin(`/api/usuarios/admin/search?q=${encodeURIComponent(q.trim())}`)
  return handleAdminResponse(res)
}

export async function markNotificacaoAdminLida(id: number): Promise<void> {
  const res = await fetchAdmin(`/api/notificacoes/admin/${id}/lida`, { method: 'PATCH' })
  await handleAdminResponse(res)
}

export async function markAllNotificacoesAdminLidas(): Promise<number> {
  const res = await fetchAdmin('/api/notificacoes/admin/marcar-todas-lidas', { method: 'PATCH' })
  const data = await handleAdminResponse<{ updated: number }>(res)
  return data?.updated ?? 0
}

export async function getFeedbacksAdmin(filtro: 'all' | 'pendente' | 'aprovado' | 'oculto' = 'all'): Promise<FeedbackItem[]> {
  const res = await fetchAdmin(`/api/feedbacks/admin?filtro=${filtro}`)
  return handleAdminResponse<FeedbackItem[]>(res)
}

export async function bulkUpdateFeedbacksAdmin(
  ids: number[],
  body: Partial<{ aprovado: boolean; ativo: boolean; destaque: boolean }>
): Promise<number> {
  const res = await fetchAdmin('/api/feedbacks/admin/bulk', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, ...body }),
  })
  const data = await handleAdminResponse<{ updated: number }>(res)
  return data?.updated ?? 0
}

export async function getSocialPostsAdmin(
  filtro: 'all' | 'pendente' | 'aprovado' | 'oculto' = 'all'
): Promise<SocialPostAdminItem[]> {
  const res = await fetchAdmin(`/api/social/posts/admin?filtro=${filtro}`)
  return handleAdminResponse<SocialPostAdminItem[]>(res)
}

export async function bulkUpdateSocialPostsAdmin(
  ids: number[],
  body: Partial<{ aprovado: boolean; ativo: boolean; destaque: boolean }>
): Promise<number> {
  const res = await fetchAdmin('/api/social/posts/admin/bulk', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, ...body }),
  })
  const data = await handleAdminResponse<{ updated: number }>(res)
  return data?.updated ?? 0
}
